/**
 * P5's admin-only "System knowledge" pane, registered into
 * `agentPanelPanes` (`views/panelPanes.ts`). Visibility comes from the
 * backend (`agentSystemKnowledgeAccess.admin`, MEIS-13520 17.3) rather than
 * a client-side group literal — the backend still enforces the admin group
 * on every admin route regardless of what this query answers, so this is
 * only about whether the button/pane are offered.
 *
 * `useSystemKnowledgeVisible` is called from three places at once while the
 * panel is open — the header button's `useVisible`, `useSystemKnowledgeBadge`,
 * and `SystemKnowledgePaneView` — so its admin answer lives in one shared,
 * identity-keyed module-level store (`accessState`, in the style of
 * `ItemDetailHintBar`'s dismissal store) instead of per-hook-instance state:
 * at most one in-flight/settled `agentSystemKnowledgeAccess` query runs per
 * identity, and every reader derives `visible` by comparing the store's own
 * `identityKey` against the caller's current `useAgentIdentityKey()` — so a
 * previous identity's `admin: true` can never be read on the render that
 * immediately follows an identity change, before the probing effect has had
 * a chance to run.
 *
 * `useAgentSystemKnowledge` does the actual lessons fetching and must run
 * exactly once: `useSystemKnowledgeBadge` is the sole call site — it is
 * mounted for as long as the header button is visible, i.e. the whole time
 * an admin has the panel open, which always outlives the pane itself being
 * open (the pane can only open by clicking that same button). It publishes
 * its state, keyed the same way as the access store, to a second
 * module-level store; the pane's own view (`Component`) reads that published
 * snapshot through `useSyncExternalStore` instead of calling the hook a
 * second time, and — like the access store — only accepts a snapshot whose
 * `identityKey` matches its own current identity.
 */
import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { Psychology as SystemKnowledgeIcon } from "@mui/icons-material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useAgentIdentityKey } from "../useAgentAccess.js";
import {
  AgentSystemKnowledge,
  useAgentSystemKnowledge,
  type AgentSystemKnowledgeState,
} from "../AgentSystemKnowledge.js";
import { hasOpenProposals } from "../agentSystemLessons.js";
import {
  AgentSystemKnowledgeAccessDocument,
  type AgentSystemKnowledgeAccessQuery,
} from "./systemKnowledgePane.generated.js";
import type { AgentPanelPane } from "./panelPanes.js";

type SystemKnowledgeAccessState = {
  identityKey: string;
  admin: boolean;
} | null;

let accessState: SystemKnowledgeAccessState = null;
/**
 * The identity the in-flight (or last completed) probe was started for —
 * guards against a second hook instance issuing another query for an
 * identity that already has one running or settled.
 */
let pendingAccessIdentityKey: string | null = null;
/**
 * How many mounted hook instances currently need the access store. Reaching
 * zero resets the store, so a later remount (e.g. the panel is closed and
 * reopened) always re-probes instead of reusing a possibly stale answer.
 */
let accessConsumerCount = 0;
const accessListeners = new Set<() => void>();

const subscribeToAccessState = (onStoreChange: () => void) => {
  accessListeners.add(onStoreChange);
  return () => {
    accessListeners.delete(onStoreChange);
  };
};

const publishAccessState = (state: SystemKnowledgeAccessState) => {
  accessState = state;
  accessListeners.forEach(listener => listener());
};

const getAccessStateSnapshot = () => accessState;

/**
 * Runs the single shared `agentSystemKnowledgeAccess` probe for the current
 * identity — at most one in-flight/settled query per identity, regardless of
 * how many consumers (header button, badge, pane view) are mounted at once.
 * Returns the store's raw state; callers must compare its `identityKey`
 * against their own before trusting `admin`, since this can still be
 * holding the previous identity's answer on the render that immediately
 * follows an `identityKey` change, before the effect below starts the new
 * probe.
 */
function useSystemKnowledgeAccessState(
  identityKey: string
): SystemKnowledgeAccessState {
  const apolloClient = useApolloClient();
  const state = React.useSyncExternalStore(
    subscribeToAccessState,
    getAccessStateSnapshot
  );

  React.useEffect(() => {
    accessConsumerCount += 1;
    return () => {
      accessConsumerCount -= 1;
      if (accessConsumerCount === 0) {
        pendingAccessIdentityKey = null;
        publishAccessState(null);
      }
    };
  }, []);

  React.useEffect(() => {
    if (
      pendingAccessIdentityKey === identityKey ||
      accessState?.identityKey === identityKey
    ) {
      return;
    }
    pendingAccessIdentityKey = identityKey;
    let cancelled = false;
    apolloClient
      .query<AgentSystemKnowledgeAccessQuery>({
        query: AgentSystemKnowledgeAccessDocument,
        fetchPolicy: "network-only",
        context: { noErrorNotification: true },
      })
      .then(({ data }) => {
        if (cancelled || pendingAccessIdentityKey !== identityKey) return;
        const result = getDataOrNull(data.agentSystemKnowledgeAccess);
        publishAccessState({ identityKey, admin: result?.admin === true });
      })
      .catch(() => {
        if (cancelled || pendingAccessIdentityKey !== identityKey) return;
        publishAccessState({ identityKey, admin: false });
      });
    return () => {
      cancelled = true;
    };
    // `identityKey` alone decides when to (re-)probe; `apolloClient` is
    // recreated with the same identity and would otherwise re-run this on
    // every render (see useAgentAccess.ts).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityKey]);

  return state;
}

/**
 * Whether the caller may see the admin-only pane, per the backend's shared
 * `agentSystemKnowledgeAccess` probe (see file header) — replaces the former
 * client-side `belongsToGroup("msys-ai-admin")` literal (MEIS-13520 17.3).
 * `false` while loading, on any error/refusal (a plain network error, or the
 * query's own `MissingCapabilities`/`MissingPermissions` refusal), or
 * whenever the shared store's answer belongs to a different identity than
 * the caller's current one — so a stale admin's state can never linger, not
 * even for a single render, while the fresh identity's answer is pending.
 */
function useSystemKnowledgeVisible(): boolean {
  const identityKey = useAgentIdentityKey();
  const access = useSystemKnowledgeAccessState(identityKey);
  return access?.identityKey === identityKey && access.admin === true;
}

type SystemKnowledgeLessonsState = {
  identityKey: string;
  state: AgentSystemKnowledgeState;
} | null;

const stateListeners = new Set<() => void>();
let latestState: SystemKnowledgeLessonsState = null;

const subscribeToSystemKnowledgeState = (onStoreChange: () => void) => {
  stateListeners.add(onStoreChange);
  return () => {
    stateListeners.delete(onStoreChange);
  };
};

const publishSystemKnowledgeState = (state: SystemKnowledgeLessonsState) => {
  latestState = state;
  stateListeners.forEach(listener => listener());
};

const getSystemKnowledgeSnapshot = () => latestState;

/**
 * The sole call site of `useAgentSystemKnowledge` — see the file header.
 * Enabled only for an admin, so a non-admin never fetches the admin-only
 * lessons (the backend would also refuse it, but there's no reason to try).
 * Publishes `null` once `visible` turns false (e.g. the selected user
 * changed to someone outside the admin group) so a stale admin's lessons
 * can never leak to `SystemKnowledgePaneView` through the shared store, and
 * drops whatever it last published as soon as it unmounts — the panel's
 * header button (and with it every pane) has gone away, so nothing should
 * be left behind for a later remount to briefly show before its own fetch
 * completes.
 */
function useSystemKnowledgeBadge(): boolean {
  const identityKey = useAgentIdentityKey();
  const visible = useSystemKnowledgeVisible();
  const state = useAgentSystemKnowledge(visible, identityKey);

  React.useEffect(() => {
    publishSystemKnowledgeState(visible ? { identityKey, state } : null);
  }, [identityKey, visible, state]);

  React.useEffect(() => {
    return () => publishSystemKnowledgeState(null);
  }, []);

  return visible && hasOpenProposals(state.lessons);
}

/**
 * Reads the state the badge already fetched; never calls the hook itself.
 * `AgentPanel` renders the active pane by id without re-checking
 * visibility, so this also re-checks it directly: it must render nothing
 * once the current user is no longer in the admin group, even while the
 * pane stays mounted (e.g. after switching the selected user). The
 * published snapshot is only trusted when its own `identityKey` matches the
 * caller's current one — cleared synchronously right here, at read time,
 * rather than only once the badge's own effect gets around to publishing
 * `null` for the new identity.
 */
function SystemKnowledgePaneView() {
  const identityKey = useAgentIdentityKey();
  const visible = useSystemKnowledgeVisible();
  const snapshot = React.useSyncExternalStore(
    subscribeToSystemKnowledgeState,
    getSystemKnowledgeSnapshot
  );
  const state =
    snapshot && snapshot.identityKey === identityKey ? snapshot.state : null;
  if (!visible || !state) return null;
  return <AgentSystemKnowledge state={state} />;
}

function useSystemKnowledgeTitle(): string {
  const { t } = useTranslate(["Assistant"]);
  return t("System knowledge (admin)", { ns: "Assistant" });
}

export const systemKnowledgePane: AgentPanelPane = {
  id: "system-knowledge",
  useTitle: useSystemKnowledgeTitle,
  icon: SystemKnowledgeIcon,
  useVisible: useSystemKnowledgeVisible,
  useBadge: useSystemKnowledgeBadge,
  Component: SystemKnowledgePaneView,
};
